import React, { forwardRef, useImperativeHandle, useState } from 'react';
import useAxios from 'axios-hooks';

import Alert from '@react/react-spectrum/Alert';
import Button from '@react/react-spectrum/Button';
import Dialog from '@react/react-spectrum/Dialog';
import Heading from '@react/react-spectrum/Heading';
import Radio from '@react/react-spectrum/Radio';
import RadioGroup from '@react/react-spectrum/RadioGroup';
import { Table, THead, TBody, TR, TH, TD } from '@react/react-spectrum/Table';
import Wait from '@react/react-spectrum/Wait';

import './SandboxSnapshotList.scss';

function SandboxSnapshotList({ orgId, sandboxName }, ref) {
  const [filter, setFilter] = useState('currentSandbox');
  const [selectedSnapshot, setSelectedSnapshot] = useState(false);

  const [{ loading, error, data }, refetch] = useAxios({
    url: `${process.env.REACT_APP_API_ROOT}/aep/snapshots/list`,
    params: { orgId, sandboxName }
  });

  useImperativeHandle(ref, () => ({
    refresh() {
      refetch();
    }
  }));

  if (loading) {
    return <Wait size="S" />;
  }

  if (error) {
    return <div>Couldn't load snapshots: {error.message}</div>;
  }

  if (!data) {
    return;
  }

  if (data.result?.length === 0) {
    return <p>No snapshots found</p>;
  }

  const snapshots = data.result
    .filter((snapshot) => {
      const properties = getSnapshotProperties(snapshot);

      if (properties.internal) {
        return false;
      }

      if (filter === 'currentSandbox') {
        return snapshot.sourceSandbox.name === sandboxName;
      } else {
        return true;
      }
    })
    .map((snapshot) => {
      const timestamp = new Date();
      timestamp.setTime(snapshot.exportTimestamp);

      return {
        ...snapshot,
        timestamp
      };
    })
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

  return (
    <div className="sandbox-snapshot-list">
      <RadioGroup
        name="snapshot-filter"
        defaultSelectedValue={filter}
        onChange={(value) => setFilter(value)}>
        <Radio label="Current sandbox" value="currentSandbox" />
        <Radio label="All snapshots" value="all" />
      </RadioGroup>

      {snapshots.length > 0 && (
        <>
          <Table>
            <THead>
              <TH>Name</TH>
              <TH>Sandbox</TH>
              <TH>Created by</TH>
              <TH>Created at</TH>
              <TH></TH>
            </THead>
            <TBody>
              {Array.isArray(snapshots) &&
                snapshots.map((snapshot) => {
                  const properties = getSnapshotProperties(snapshot);

                  return (
                    <TR key={snapshot.snapshotId}>
                      <TD>
                        <strong>{snapshot.name}</strong>
                        <small>{properties.description}</small>
                      </TD>
                      <TD>{snapshot.sourceSandbox.name}</TD>
                      <TD>{properties.userId}</TD>
                      <TD>{snapshot.timestamp.toLocaleString()}</TD>
                      <TD>
                        <Button
                          quiet
                          onClick={() => setSelectedSnapshot(snapshot)}>
                          Import
                        </Button>
                      </TD>
                    </TR>
                  );
                })}
            </TBody>
          </Table>

          <ImportSnapshotDialog
            isOpen={selectedSnapshot}
            close={() => setSelectedSnapshot(null)}
            orgId={orgId}
            sandboxName={sandboxName}
            snapshot={selectedSnapshot}
          />
        </>
      )}

      {snapshots.length === 0 && <p>No snapshots found</p>}
    </div>
  );
}

function getSnapshotProperties(snapshot) {
  try {
    return JSON.parse(snapshot.description);
  } catch {
    return {};
  }
}

export default forwardRef(SandboxSnapshotList);

function ImportSnapshotDialog({ isOpen, close, orgId, sandboxName, snapshot }) {
  const [{ data, loading, error }, execute] = useAxios(
    {
      url: `${process.env.REACT_APP_API_ROOT}/aep/snapshots/import`,
      method: 'POST'
    },
    { manual: true }
  );

  const initialState = !data && !error;

  // API may return a correct response but with an error message inside
  const apiError = data?.result?.type === 'ERROR';

  const onConfirm = () => {
    if (!initialState) {
      close();
      return;
    }

    if (snapshot) {
      execute({
        data: {
          sandboxName,
          orgId,
          snapshotId: snapshot.snapshotId
        }
      });
    }
  };

  return (
    isOpen && (
      <div className="spectrum-Underlay is-open">
        <Dialog
          className="import-snapshot-dialog"
          title="Import snapshot"
          confirmLabel={initialState ? 'Import' : 'Close'}
          cancelLabel={initialState ? 'Cancel' : null}
          onConfirm={onConfirm}
          onCancel={() => close()}>
          {loading && (
            <div className="wait-container">
              <Wait centered />
            </div>
          )}

          {data && !apiError && (
            <div className="result-container">
              <Alert variant="success" header="Success">
                Snapshot import request was successful.
                <br />
                Depending on the contents of selected snapshot, it usually takes
                between 10 seconds to few minutes.
              </Alert>
            </div>
          )}

          {(error || apiError) && (
            <div className="result-container">
              <Alert variant="error" header="Error">
                An error occured when trying to import a snapshot.
              </Alert>
            </div>
          )}

          {initialState && !loading && (
            <>
              <Alert variant="info" header="Notice">
                Snapshot can only be imported into an empty sandbox.
                <br />
                If this sandbox was not reset recently, snapshot import will
                fail.
              </Alert>

              <div>
                <Heading variant="subtitle3">Snapshot</Heading>
                <p>{snapshot.name}</p>
              </div>
            </>
          )}
        </Dialog>
      </div>
    )
  );
}
